import { io } from 'socket.io-client';

let rootSocket = null;
let chatSocket = null;

const WS_URL = import.meta.env.VITE_WS_URL || 'http://localhost:8080';
const TOKEN_KEY = 'symptoconnect_token';

export function connect() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) {
    console.warn('No auth token found for socket connection');
  }

  // Root namespace (for “newSymptom”, notifications, etc.)
  // Guard: socket.active is true while connecting OR connected — prevents duplicate
  // sockets when connect() is called before the handshake completes.
  if (!rootSocket || !rootSocket.active) {
    if (rootSocket) {
      // Leftover disconnected instance — tear it down cleanly before replacing
      rootSocket.removeAllListeners();
      rootSocket.disconnect();
    }
    rootSocket = io(WS_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 10,
      timeout: 20000,
    });

    rootSocket.on('connect', () => {
      console.log('✅ Connected to root namespace', { id: rootSocket.id });
    });

    rootSocket.on('disconnect', (reason) => {
      console.log('❌ Root namespace disconnected:', reason);
    });
  }

  // Chat namespace (room-per-symptom)
  if (!chatSocket || !chatSocket.active) {
    if (chatSocket) {
      chatSocket.removeAllListeners();
      chatSocket.disconnect();
    }
    chatSocket = io(`${WS_URL}/chat`, {
      auth: { token },
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 10,
      timeout: 20000,
    });

    chatSocket.on('connect', () => {
      console.log('✅ Connected to /chat namespace', { id: chatSocket.id });
    });

    chatSocket.on('disconnect', (reason) => {
      console.log('❌ /chat namespace disconnected:', reason);
    });
  }

  return { rootSocket, chatSocket };
}

export function getSocket(ns = 'root') {
  // Ensure sockets are created if they don't exist
  if (!rootSocket && !chatSocket) {
    console.log('🔌 No sockets exist, creating new connections...');
    connect();
  }
  
  const socket = ns === 'chat' ? chatSocket : rootSocket;
  if (!socket) {
    console.warn(`⚠️ ${ns} socket is null, attempting to reconnect...`);
    connect();
    return ns === 'chat' ? chatSocket : rootSocket;
  }
  
  return socket;
}

export function joinRoom(roomId, ack) {
  if (!chatSocket) {
    console.log('🔄 Chat socket not available, connecting...');
    connect();
  }
  
  if (!chatSocket) {
    console.error('❌ Failed to establish chat socket connection');
    if (typeof ack === 'function') ack({ ok: false, error: 'Socket connection failed' });
    return;
  }
  
  console.log('🚪 Joining room:', roomId);
  chatSocket.emit('joinRoom', { roomId }, (resp) => {
    console.log('🚪 Join room response:', resp);
    if (typeof ack === 'function') ack(resp);
  });
}

export function sendMessage(roomId, text, ack) {
  if (!chatSocket) {
    console.log('🔄 Chat socket not available, connecting...');
    connect();
  }
  
  if (!chatSocket) {
    console.error('❌ Failed to establish chat socket connection');
    if (typeof ack === 'function') ack({ ok: false, error: 'Socket connection failed' });
    return;
  }
  
  console.log('💬 Sending message to room:', roomId, 'Text:', text);
  chatSocket.emit('message', { roomId, text }, (resp) => {
    console.log('💬 Send message response:', resp);
    if (typeof ack === 'function') ack(resp);
  });
}

// Helper functions for room ID generation
export function createGeneralRoom(symptomId) {
  return `symptom_${symptomId}`;
}

export function createPrivateRoom(symptomId, doctorId) {
  return `symptom_${symptomId}_doctor_${doctorId}`;
}

export function disconnect() {
  if (rootSocket) { rootSocket.disconnect(); rootSocket = null; }
  if (chatSocket) { chatSocket.disconnect(); chatSocket = null; }
}
